import type { ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { Drawer } from '@/components/ui/Overlay'
import { ReviewActions } from './ReviewActions'
import { FavoriteToggle } from './FavoriteToggle'
import { FlagToggle } from './FlagToggle'
import { TagEditor } from './TagEditor'
import { PreviouslyRejectedFlag, VerdictHistory } from './VerdictHistory'
import type { Reviewable } from '@/types'

/**
 * The four review drawers differ only in their field bodies. The rejection
 * flag, the tags block, the marker row, the verdict history, and the footer
 * are the same every time, so they live here (docs/06-component-refactor.md §4.3).
 *
 * Order is fixed: rejection flag → fields → tags → marker → history. The
 * marker is Favorite, or Flag when `onFlag` is given (programs, APP-121).
 */
export function ReviewDrawer({
  open,
  onClose,
  title,
  subtitle,
  creating,
  entity,
  width,
  saveLabel,
  saveDisabled,
  onSave,
  onApprove,
  onReject,
  onFavorite,
  onFlag,
  tags,
  tagPool,
  onTagsChange,
  /** Delete, re-check, and other per-entity controls; sits in the marker row. */
  extraActions,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: ReactNode
  creating: boolean
  /** null while creating. */
  entity: Reviewable | null
  width?: string
  saveLabel?: string
  /** A required field is blank. */
  saveDisabled?: boolean
  onSave: () => void
  onApprove: () => void
  onReject: () => void
  onFavorite?: (favorite: boolean) => void
  /** Replaces the Favorite row with a Flag row. */
  onFlag?: (flagged: boolean) => void
  tags: string[]
  tagPool: string[]
  onTagsChange: (tags: string[]) => void
  extraActions?: ReactNode
  children: ReactNode
}) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width={width}
      footer={
        <>
          {entity && (
            <ReviewActions
              status={entity.status}
              onApprove={onApprove}
              onReject={onReject}
              className="mr-auto"
            />
          )}
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" disabled={saveDisabled} onClick={onSave}>
            {saveLabel ?? (creating ? 'Add' : 'Save changes')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {entity?.previouslyRejected && <PreviouslyRejectedFlag />}

        {children}

        <div className="space-y-1.5">
          <span className="field-label block">Tags</span>
          <TagEditor tags={tags} suggestions={tagPool} onChange={onTagsChange} />
        </div>

        {entity && (
          <>
            <div className="flex items-center gap-2 border-t border-rule pt-4">
              {onFlag ? (
                <>
                  <span className="field-label">Flag</span>
                  <FlagToggle flagged={entity.flagged} onChange={onFlag} />
                </>
              ) : (
                onFavorite && (
                  <>
                    <span className="field-label">Favorite</span>
                    <FavoriteToggle favorite={entity.favorite} onChange={onFavorite} />
                  </>
                )
              )}
              {extraActions && <span className="ml-auto flex gap-1.5">{extraActions}</span>}
            </div>
            <div className="border-t border-rule pt-4">
              <VerdictHistory verdicts={entity.verdicts} entityHash={entity.entityHash} />
            </div>
          </>
        )}
      </div>
    </Drawer>
  )
}
